import React, { useState, useEffect } from "react";
import { Row, Col, Table, Tag, Card, Statistic, Progress, Alert, Typography, Spin, Badge, Tooltip, DatePicker, Button, Space } from "antd";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Legend,
} from "recharts";
import {
    WarningOutlined,
    DollarOutlined,
    ShoppingOutlined,
    DeleteOutlined,
    SwapOutlined,
    StockOutlined,
    ArrowUpOutlined,
    ClockCircleOutlined,
    ArrowDownOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    CalendarOutlined,
    ReloadOutlined,
    EuroOutlined,
} from "@ant-design/icons";
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import ReportsApi from "../../common/Apis/ReportsApi";

// Extend dayjs with required plugins
dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;

const OperationalDashboard = () => {
    const [loading, setLoading] = useState(false);
    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [dateError, setDateError] = useState('');

    const [weeklyData, setWeeklyData] = useState([
        {
            week: "Last Week",
            goodsUsed: 0,
            rawWaste: 0,
            productionWaste: 0,
            totalCogs: 0,
            stockAdjustments: 0,
        },
        {
            week: "This Week",
            goodsUsed: 0,
            rawWaste: 0,
            productionWaste: 0,
            totalCogs: 0,
            stockAdjustments: 0,
        },
    ]);

    const [kpis, setKpis] = useState({
        totalCogs: 0,
        totalWaste: 0,
        goodsUsed: 0,
        stockAdjustments: 0,
        trendPctChange: 0,
        totalWaste: 0,
        productionWaste: 0,
        wastePercentage: 0,
    });

    const [wasteBreakdown, setWasteBreakdown] = useState([
        { name: "Goods Used", value: 0, color: "#52c41a" },
        { name: "Raw Waste", value: 0, color: "#faad14" },
        { name: "Production Waste", value: 0, color: "#ff7c43" },
    ]);
   const [wasteThresholds, setWasteThresholds] = useState({
        productionWasteExceedsThreshold: false,
        rawWasteExceedsThreshold: false,
    });

    const [unitHeatMapData, setUnitHeatMapData] = useState([]);

    const [hotHoldData, setHotHoldData] = useState({
        units: [],
        overallCompliancePct: 0,
        totalExpected: 0,
        totalCompleted: 0,
        overallStatus: "GREY"
    });


    const [cleaningCompliance, setCleaningCompliance] = useState({
        percent: 0,
        tasksCompleted: 0,
        totalTasks: 0,
        arcColour: "RED",
        heatTiles: []
    });

    const [openingAndClosingStockData, setOpeningAndClosingStockData] = useState({
        weekId: 0,
        openingStockDate: 0,
        openingStockValue: 0,
        closingStockDate: 0,
        closingStockValue: 0,
        isLoading: false
    });

    // Helper function to convert date to GMT milliseconds epoch (UTC+00)
    const dateToGmtMillis = (date) => {
        if (!date) return null;
        const utcDate = dayjs(date).utc();
        const startOfDay = utcDate.startOf('day');
        return startOfDay.valueOf();
    };


    // Get Sunday of current week (start of week) in UTC
    const getSundayOfCurrentWeek = () => {
        const now = dayjs().utc();
        const dayOfWeek = now.day(); // 0 = Sunday, 1 = Monday, etc.
        const sunday = now.subtract(dayOfWeek, 'day');
        return sunday.startOf('day');
    };

   // Get Sunday of current week (end of week) in UTC
    const getSundayOfNextWeek = () => {
        const sunday = getSundayOfCurrentWeek();
        const endDate = sunday.add(7, 'day');
        return endDate.startOf('day');
    };

    // Get current week range as GMT milliseconds epoch
    const getCurrentWeekRange = () => {
        const startDate = getSundayOfCurrentWeek();
        const endDate = getSundayOfNextWeek();

        return {
            start: startDate.valueOf(),
            end: endDate.valueOf()
        };
    };

    const disabledStartDate = (current) => {
        if (!current) return false;
        return current.day() !== 0; // Sunday = 0
    };

    const disabledEndDate = (current) => {
        if (!current) return false;
        if (current.day() !== 0) return true;
        if (selectedStartDate && !current.isAfter(selectedStartDate, 'day')) return true;
        if (selectedStartDate && current.isAfter(selectedStartDate.add(7, 'day'), 'day')) return true;
        return false;
    };

    // Handle start date change
    const handleStartDateChange = (date) => {
        setSelectedStartDate(date);
        setSelectedEndDate(null);
        setDateError('');
        
        // Auto-set end date to Sunday of the same week if start date is selected
        if (date) {
            const endDate = date.add(7, 'day');
            setSelectedEndDate(endDate);
        }
    };

    // Handle end date change
    const handleEndDateChange = (date) => {
        setSelectedEndDate(date);
        setDateError('');
    };


    // Get percentage from status
    const getStatusPercentage = (status) => {
        switch (status) {
            case 'GREEN':
                return 100;
            case 'AMBER':
            case 'WARNING':
                return 75;
            case 'ORANGE':
                return 50;
            case 'RED':
                return 25;
            case 'GREY':
            default:
                return 0;
        }
    };


    // Fetch unit heat grid data
    const fetchUnitHeatGrid = async (start, end) => {
        try {
            console.log("Fetching unit heat grid for milliseconds epoch:", {
                start: start,
                end: end,
                startDate: new Date(start).toLocaleString(),
                endDate: new Date(end).toLocaleString()
            });

            const response = await ReportsApi.DashboardUnitHeatGrid(start, end);

            console.log("Unit Heat Grid API Response:", response);

            if (response?.resultStatus === "SUCCESSFUL" && response?.payload?.units) {
                const units = response.payload.units;

                const transformedData = units.map((unit) => {
                    const checks = unit.cells.map((cell) => getStatusPercentage(cell.status));

                    return {
                        unit: unit.unitLabel,
                        checks: checks,
                        status: unit.cells.some(cell => cell.status === 'RED') ? 'critical' :
                            unit.cells.some(cell => cell.status === 'ORANGE') ? 'warning' : 'good',
                        rawData: unit.cells
                    };
                });

                setUnitHeatMapData(transformedData);
            } else if (response?.payload && Array.isArray(response?.payload)) {
                const transformedData = response.payload.map((item) => ({
                    unit: item.unitName || item.unit || item.name,
                    checks: item.complianceData || item.checks || item.dailyCompliance || [],
                    status: item.status || "good"
                }));
                setUnitHeatMapData(transformedData);
            } else {
                console.warn("Unexpected API response format, using default data");
                setUnitHeatMapData([
                    { unit: "Unit 1", status: "good", checks: [100, 95, 98, 92, 96, 94, 97] },
                    { unit: "Unit 2", status: "good", checks: [88, 85, 90, 87, 86, 89, 91] },
                    { unit: "Unit 3", status: "good", checks: [92, 94, 90, 88, 93, 91, 89] },
                    { unit: "Unit 4", status: "warning", checks: [78, 75, 72, 80, 76, 74, 73] },
                    { unit: "Unit 5", status: "good", checks: [85, 87, 84, 86, 88, 85, 89] },
                    { unit: "Unit 6", status: "good", checks: [90, 92, 88, 91, 89, 93, 90] },
                    { unit: "Unit 0", status: "critical", checks: [45, 50, 48, 42, 46, 44, 43] },
                ]);
            }

        } catch (error) {
            console.error("Error fetching unit heat grid data:", error);
            setUnitHeatMapData([
                { unit: "Unit 1", status: "good", checks: [100, 95, 98, 92, 96, 94, 97] },
                { unit: "Unit 2", status: "good", checks: [88, 85, 90, 87, 86, 89, 91] },
                { unit: "Unit 3", status: "good", checks: [92, 94, 90, 88, 93, 91, 89] },
                { unit: "Unit 4", status: "warning", checks: [78, 75, 72, 80, 76, 74, 73] },
                { unit: "Unit 5", status: "good", checks: [85, 87, 84, 86, 88, 85, 89] },
                { unit: "Unit 6", status: "good", checks: [90, 92, 88, 91, 89, 93, 90] },
                { unit: "Unit 0", status: "critical", checks: [45, 50, 48, 42, 46, 44, 43] },
            ]);
        }
    };

        const fetchHotHoldChecks = async (start, end) => {
        try {
            console.log("Fetching hot hold compliance for milliseconds epoch:", {
                start: start,
                end: end,
                startDate: new Date(start).toLocaleString(),
                endDate: new Date(end).toLocaleString()
            });

            const response = await ReportsApi.DashboardHotHoldCompliance(start, end);

            console.log("Hot Hold Compliance API Response:", response);

            if (response?.resultStatus === "SUCCESSFUL" && response?.payload) {
                const payload = response.payload;
                
                console.log("Hot hold payload:", payload);
                console.log("Units array:", payload.units);
                console.log("Number of units:", payload.units?.length);
                
                // Calculate totals from units
                let totalExpected = 0;
                let totalCompleted = 0;
                
                // Process units and ensure each has overallStatus
                const processedUnits = (payload.units || []).map(unit => {
                    totalExpected += unit.totalExpected || 0;
                    totalCompleted += unit.totalCompleted || 0;
                    
                    // Calculate unit compliance percentage
                    const unitCompliance = unit.totalExpected > 0 
                        ? (unit.totalCompleted / unit.totalExpected) * 100 
                        : 0;
                    
                    // Determine unit status based on compliance percentage
                    let unitStatus = unit.overallStatus || "GREY";
                    if (!unit.overallStatus || unit.overallStatus === "GREY") {
                        if (unitCompliance >= 100) {
                            unitStatus = "GREEN";
                        } else if (unitCompliance >= 70) {
                            unitStatus = "AMBER";
                        } else if (unitCompliance > 0) {
                            unitStatus = "RED";
                        } else {
                            unitStatus = "GREY";
                        }
                    }
                    
                    // Also process days to ensure they have status
                    const processedDays = (unit.days || [])
                        .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
                        .slice(0, 7)
                        .map(day => {
                        let dayStatus = day.status || "GREY";
                        if (!day.status || day.status === "GREY") {
                            if (day.expectedChecks === 0) {
                                dayStatus = "GREY";
                            } else if (day.completedChecks >= day.expectedChecks) {
                                dayStatus = "GREEN";
                            } else if (day.completedChecks >= day.expectedChecks * 0.7) {
                                dayStatus = "AMBER";
                            } else if (day.completedChecks > 0) {
                                dayStatus = "RED";
                            } else {
                                dayStatus = "RED";
                            }
                        }
                        return {
                            ...day,
                            status: dayStatus
                        };
                    });
                    
                    return {
                        ...unit,
                        overallStatus: unitStatus,
                        days: processedDays
                    };
                });
                
                // Calculate overall compliance percentage
                const compliancePct = payload.overallCompliancePct || 0;
                
                // Determine overall status
                let overallStatus = "GREY";
                if (processedUnits.length > 0) {
                    if (compliancePct >= 100) {
                        overallStatus = "GREEN";
                    } else if (compliancePct >= 70) {
                        overallStatus = "AMBER";
                    } else if (compliancePct > 0) {
                        overallStatus = "RED";
                    } else {
                        overallStatus = "GREY";
                    }
                }
                
                setHotHoldData({
                    units: processedUnits,
                    overallCompliancePct: compliancePct,
                    totalExpected: totalExpected,
                    totalCompleted: totalCompleted,
                    overallStatus: overallStatus
                });
                
                console.log("HotHoldData set:", {
                    units: processedUnits.length,
                    overallCompliancePct: compliancePct,
                    totalExpected: totalExpected,
                    totalCompleted: totalCompleted,
                    overallStatus: overallStatus
                });
            } else {
                console.warn("Hot hold compliance API response was not successful");
                setHotHoldData({
                    units: [],
                    overallCompliancePct: 0,
                    totalExpected: 0,
                    totalCompleted: 0,
                    overallStatus: "GREY"
                });
            }

        } catch (error) {
            console.error("Error fetching hot hold compliance data:", error);
            setHotHoldData({
                units: [],
                overallCompliancePct: 0,
                totalExpected: 0,
                totalCompleted: 0,
                overallStatus: "GREY"
            });
        }
    };

    const getStatusIcon = (status) => {
    switch (status) {
        case 'GREEN':
            return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
        case 'AMBER':
        case 'WARNING':
            return <ExclamationCircleOutlined style={{ color: "#faad14" }} />;
        case 'ORANGE':
            return <WarningOutlined style={{ color: "#fa8c16" }} />;
        case 'RED':
            return <CloseCircleOutlined style={{ color: "#ff4d4f" }} />;
        case 'GREY':
        default:
            return <ClockCircleOutlined style={{ color: "#d9d9d9" }} />;
    }
};

    // Fetch cleaning compliance data
    const fetchCleaningCompliance = async () => {
        try {
            const yesterday = dayjs().utc().subtract(1, "day");
            const start = dateToGmtMillis(yesterday.toDate());
            const end = dayjs(yesterday).utc().endOf('day').valueOf();

            const response = await ReportsApi.DashboardCleaningCompliance(start, end);

            console.log("Cleaning Compliance API Response:", response);

            if (response?.resultStatus === "SUCCESSFUL" && response?.payload) {
                const payload = response.payload;

                setCleaningCompliance({
                    percent: payload.compliancePct || 0,
                    tasksCompleted: payload.completedTasks || 0,
                    totalTasks: payload.totalTasks || 0,
                    arcColour: payload.arcColour || "RED",
                    heatTiles: payload.heatTiles || [],
                });
            } else {
                console.warn("Cleaning compliance API response was not successful or had unexpected format");
                setCleaningCompliance({
                    percent: 0,
                    tasksCompleted: 0,
                    totalTasks: 0,
                    arcColour: "RED",
                    heatTiles: [],
                });
            }
        } catch (error) {
            console.error("Error fetching cleaning compliance data:", error);
            setCleaningCompliance({
                percent: 0,
                tasksCompleted: 0,
                totalTasks: 0,
                arcColour: "RED",
                heatTiles: [],
            });
        }
    };

    // Fetch opening stock data
const fetchOpeningStock = async (start, end) => {
    try {
        setOpeningAndClosingStockData(prev => ({ ...prev, isLoading: true }));

        const response = await ReportsApi.DashboardOpeningStock(start, end);

        if (response?.resultStatus === "SUCCESSFUL" && response?.payload) {
            const payload = response.payload;
            
            setOpeningAndClosingStockData({
                weekId: payload.weekId || 0,
                openingStockDate: payload.openingStockDate || 0,
                openingStockValue: payload.openingStockValue || 0,
                closingStockDate: payload.closingStockDate || 0,
                closingStockValue: payload.closingStockValue || 0,
                isLoading: false
            });
        } else {
            console.warn("Opening stock API response was not successful");
            setOpeningAndClosingStockData(prev => ({ ...prev, isLoading: false }));
        }

    } catch (error) {
        console.error("Error fetching opening stock data:", error);
        setOpeningAndClosingStockData(prev => ({ ...prev, isLoading: false }));
    }
};

    // Fetch waste breakdown data
    const fetchWasteBreakdown = async (start, end) => {
        try {
            console.log("Fetching waste breakdown for milliseconds epoch:", {
                start: start,
                end: end,
                startDate: new Date(start).toLocaleString(),
                endDate: new Date(end).toLocaleString()
            });

            const response = await ReportsApi.DashboardWasteBreakdown(start, end);

            console.log("Waste Breakdown API Response:", response);

            const payload = response?.payload;

            if (payload) {
                const breakdownData = [
                    { name: "Goods Used", value: payload.goodsUsedPct || 0, color: "#52c41a" },
                    { name: "Raw Waste", value: payload.rawWastePct || 0, color: "#faad14" },
                    { name: "Production Waste", value: payload.productionWastePct || 0, color: "#ff7c43" },
                ];

                setWasteBreakdown(breakdownData);

                setWasteThresholds({
                    productionWasteExceedsThreshold: payload.productionWasteExceedsThreshold || false,
                    rawWasteExceedsThreshold: payload.rawWasteExceedsThreshold || false,
                });
            }

        } catch (error) {
            console.error("Error fetching waste breakdown data:", error);
        }
    };

    // Fetch weekly COGS data
    const fetchWeeklyCogsData = async (start, end) => {
        try {
            console.log("Fetching COGS data for milliseconds epoch:", {
                start: start,
                end: end,
                startDate: new Date(start).toLocaleString(),
                endDate: new Date(end).toLocaleString()
            });

            const response = await ReportsApi.DashboardWeeklyCogs(start, end);

            console.log("COGS API Response:", response);

            if (response?.resultStatus === "SUCCESSFUL" && response?.payload) {
                const payload = response.payload;

                const lastWeekTotalWaste = (payload.lastWeekTotalWaste);
                const thisWeekTotalWaste = (payload.thisWeekTotalWaste);

                const wastePercentage = payload.thisWeekTotalCogs > 0
                    ? (thisWeekTotalWaste / payload.thisWeekTotalCogs) * 100
                    : 0;

                setKpis({
                    totalCogs: payload.thisWeekTotalCogs || 0,
                    goodsUsed: payload.thisWeekGoodsUsed || 0,
                    totalWaste: thisWeekTotalWaste,
                    productionWaste: payload.thisWeekProductionWaste,
                    rawWaste: payload.thisWeekRawWaste || 0,
                    stockAdjustments: payload.thisWeekStockAdjustments,
                    trendPctChange: payload.trendPctChange || 0,
                    productionWaste: payload.thisWeekProductionWaste,
                    wastePercentage: wastePercentage,
                    rawWastePercentage: payload.thisWeekRawWaste > 0 && payload.thisWeekTotalCogs > 0
                        ? (payload.thisWeekRawWaste / payload.thisWeekTotalCogs) * 100
                        : 0,
                });

                setWeeklyData([
                    {
                        week: "Last Week",
                        goodsUsed: payload.lastWeekGoodsUsed || 0,
                        rawWaste: payload.lastWeekRawWaste || 0,
                        productionWaste: payload.lastWeekProductionWaste || 0,
                        totalCogs: payload.lastWeekTotalCogs || 0,
                        stockAdjustments: payload.lastWeekStockAdjustments
                    },
                    {
                        week: "This Week",
                        goodsUsed: payload.thisWeekGoodsUsed || 0,
                        rawWaste: payload.thisWeekRawWaste || 0,
                        productionWaste: payload.thisWeekProductionWaste || 0,
                        totalCogs: payload.thisWeekTotalCogs || 0,
                        stockAdjustments: payload.thisWeekStockAdjustments

                    },
                ]);
            } else {
                console.error("API response was not successful:", response);
            }

        } catch (error) {
            console.error("Error fetching weekly COGS data:", error);
        }
    };

    // Fetch all data with selected date range
    const fetchAllData = async (start, end) => {
        setLoading(true);
        try {
            await Promise.all([
                fetchWeeklyCogsData(start, end),
                fetchWasteBreakdown(start, end),
                fetchUnitHeatGrid(start, end),
                fetchHotHoldChecks(start, end),
                fetchCleaningCompliance(start, end),
                fetchOpeningStock(start, end) 
            ]);
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Handle apply button click
    const handleApplyDateRange = () => {
         if (!selectedStartDate) {
            setDateError('Please select a start date (Sunday)');
            return;
        }
        if (!selectedEndDate) {
            setDateError('Please select an end date (Sunday)');
            return;
        }

        if (!selectedEndDate.isAfter(selectedStartDate, 'day')) {
            setDateError('End date must be after start date');
            return;
        }
        
        
        // Validate that dates are in the same week
        if (selectedEndDate.isAfter(selectedStartDate.add(7, 'day'), 'day')) {
            setDateError('Start and end dates must be in the same week');
            return;
        }
        
        setDateError('');
        
        // Convert to GMT milliseconds epoch
        const startEpoch = dateToGmtMillis(selectedStartDate.toDate());
        const endEpoch = dateToGmtMillis(selectedEndDate.toDate());

        fetchAllData(startEpoch, endEpoch);
    };

    // Handle reset to current week
    const handleResetToCurrentWeek = () => {
        const currentWeek = getCurrentWeekRange();
        const startDate = dayjs(currentWeek.start).utc();
        const endDate = dayjs(currentWeek.end).utc();
        
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);
        setDateError('');
        
        fetchAllData(currentWeek.start, currentWeek.end);
    };

    // Initialize with current week
    useEffect(() => {
        const currentWeek = getCurrentWeekRange();
        const startDate = dayjs(currentWeek.start).utc();
        const endDate = dayjs(currentWeek.end).utc();
        
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);
        
        fetchAllData(currentWeek.start, currentWeek.end);
    }, []);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <Spin size="large" tip="Loading dashboard data..." />
            </div>
        );
    }

    const goodsUsedPercentage = kpis.totalCogs > 0 ? ((kpis.goodsUsed / kpis.totalCogs) * 100).toFixed(1) : 0;
    const productionWastePercentage = kpis.totalCogs > 0 ? ((kpis.productionWaste / kpis.totalCogs) * 100).toFixed(1) : 0;
    const stockAdjustmentPercentage = kpis.totalCogs > 0 ? ((kpis.stockAdjustments / kpis.totalCogs) * 100).toFixed(1) : 0;

    const getArcColor = () => {
        if (cleaningCompliance.arcColour === "GREEN") return "#52c41a";
        if (cleaningCompliance.arcColour === "AMBER") return "#faad14";
        if (cleaningCompliance.arcColour === "ORANGE") return "#fa8c16";
        return "#ff4d4f";
    };

   const renderHotHoldGrid = () => {
    const { units, overallCompliancePct, overallStatus, totalExpected, totalCompleted } = hotHoldData;

    const getGridColorCode = (status) => {
        switch (status) {
            case "GREEN":
                return "#52c41a";
            case "AMBER":
                return "#faad14";
            case "ORANGE":
                return "#fa8c16";
            case "RED":
                return "#ff4d4f";
            case "GREY":
            default:
                return "#d9d9d9";
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'GREEN':
                return "Pass";
            case 'AMBER':
            case 'WARNING':
                return "Warning";
            case 'ORANGE':
                return "Needs Attention";
            case 'RED':
                return "Bad";
            case 'GREY':
            default:
                return "No Data";
        }
    };

    // Helper function to get day of week label from date
    const getDayLabel = (dateValue) => {
        if (!dateValue) return null;
        
        let dateObj;
        if (typeof dateValue === 'number') {
            dateObj = new Date(dateValue);
        } else if (typeof dateValue === 'string') {
            dateObj = new Date(dateValue);
        } else {
            return null;
        }
        
        if (isNaN(dateObj.getTime())) return null;
        
        // Get day of week (0 = Sunday, 1 = Monday, etc.)
        const dayOfWeek = dateObj.getDay();
        const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        return days[dayOfWeek];
    };

    // Check if units exist and have data
    if (!units || units.length === 0) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
                <Text type="secondary">No hot holding checks data available</Text>
            </div>
        );
    }

    return (
        <div>
            {/* Overall Summary */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '12px 16px',
                background: '#fafafa',
                borderRadius: '8px',
                marginBottom: '16px',
                border: '1px solid #e8e8e8'
            }}>
                <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Overall Compliance</Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Text style={{ fontSize: 20, fontWeight: 700, color: getGridColorCode(overallStatus) }}>
                            {overallCompliancePct.toFixed(0)}%
                        </Text>
                        <Tag 
                            color={overallStatus === 'GREEN' ? 'success' : overallStatus === 'RED' ? 'error' : overallStatus === 'AMBER' ? 'warning' : 'default'}
                            icon={getStatusIcon(overallStatus)}
                        >
                            {getStatusLabel(overallStatus)}
                        </Tag>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '16px' }}>
                    <div style={{ textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>Expected</Text>
                        <div><Text strong style={{ fontSize: 16 }}>{totalExpected}</Text></div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>Completed</Text>
                        <div><Text strong style={{ fontSize: 16, color: '#52c41a' }}>{totalCompleted}</Text></div>
                    </div>
                </div>
            </div>

            {/* Units Grid */}
            {units.map((unit) => (
                <div key={unit.storageId} style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8 }}>
                        <Text strong style={{ fontSize: 13 }}>
                            {unit.storageName || `Storage ${unit.storageId}`}
                        </Text>
                        <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                            {unit.totalCompleted || 0}/{unit.totalExpected || 0} checks
                        </Text>
                        <Tag 
                            color={unit.overallStatus === 'GREEN' ? 'success' : unit.overallStatus === 'RED' ? 'error' : unit.overallStatus === 'AMBER' ? 'warning' : 'default'}
                            style={{ marginLeft: 8 }}
                            icon={getStatusIcon(unit.overallStatus)}
                        >
                            {getStatusLabel(unit.overallStatus)}
                        </Tag>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-start", gap: 8, flexWrap: "wrap" }}>
                        {unit.days &&
                            [...unit.days]
                                .sort((a, b) => {
                                // Monday (1) -> Sunday (0)
                                const dayA = dayjs(a.date).day();
                                const dayB = dayjs(b.date).day();

                                const orderA = dayA === 0 ? 7 : dayA;
                                const orderB = dayB === 0 ? 7 : dayB;

                                return orderA - orderB;
                                })
                                .map((day, index) => {
                            // Get the correct day label from the date
                            let dayLabel = getDayLabel(day.date);
                            
                            // If no date available, use the index as fallback
                            if (!dayLabel) {
                                const fallbackDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
                                dayLabel = fallbackDays[index] || `Day ${index + 1}`;
                            }
                            
                            // Format the date for tooltip
                            let dateDisplay = 'Unknown';
                            if (day.date) {
                                try {
                                    const dateObj = typeof day.date === 'number' ? new Date(day.date) : new Date(day.date);
                                    if (!isNaN(dateObj.getTime())) {
                                        dateDisplay = dateObj.toLocaleDateString('en-GB', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric'
                                        });
                                    }
                                } catch (e) {
                                    // If date parsing fails, use the original value
                                    dateDisplay = String(day.date);
                                }
                            }
                            
                            return (
                                <Tooltip
                                    key={index}
                                    title={`${dateDisplay}: ${day.completedChecks || 0}/${day.expectedChecks || 0} checks - ${day.status || 'GREY'}`}
                                >
                                    <div
                                        style={{
                                            width: 61,
                                            height: 61,
                                            borderRadius: 6,
                                            backgroundColor: getGridColorCode(day.status || 'GREY'),
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: day.status === "GREY" || !day.status ? "#666" : "#fff",
                                            fontSize: 10,
                                            fontWeight: 600,
                                            cursor: "pointer",
                                            transition: "transform 0.2s",
                                            padding: 2
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = "scale(1.05)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = "scale(1)";
                                        }}
                                    >
                                        <div style={{ fontSize: 11, fontWeight: 700 }}>
                                            {dayLabel}
                                        </div>
                                        <div style={{ fontSize: 9 }}>
                                            {day.completedChecks || 0}/{day.expectedChecks || 0}
                                        </div>
                                    </div>
                                </Tooltip>
                            );
                        })}
                    </div>
                </div>
            ))}

            {/* Legend */}
            <div style={{ 
                marginTop: 16, 
                paddingTop: 12, 
                borderTop: "1px solid #f0f0f0",
                display: "flex",
                justifyContent: "center",
                gap: 12,
                flexWrap: "wrap"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <div style={{ width: 12, height: 12, backgroundColor: "#52c41a", borderRadius: 3 }} />
                    <Text style={{ fontSize: 11 }}>Pass</Text>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <div style={{ width: 12, height: 12, backgroundColor: "#faad14", borderRadius: 3 }} />
                    <Text style={{ fontSize: 11 }}>Warning</Text>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <div style={{ width: 12, height: 12, backgroundColor: "#fa8c16", borderRadius: 3 }} />
                    <Text style={{ fontSize: 11 }}>Attention</Text>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <div style={{ width: 12, height: 12, backgroundColor: "#ff4d4f", borderRadius: 3 }} />
                    <Text style={{ fontSize: 11 }}>Bad</Text>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <div style={{ width: 12, height: 12, backgroundColor: "#d9d9d9", borderRadius: 3 }} />
                    <Text style={{ fontSize: 11 }}>No Data</Text>
                </div>
            </div>
        </div>
    );
};

    return (
        <>
            {/* Date Range Picker */}
            <div style={{ marginBottom: 24, padding: '16px', background: '#fafafa', borderRadius: '8px', border: '1px solid #e8e8e8' }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} md={8}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <CalendarOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                            <Text strong>Select Week:</Text>
                        </div>
                    </Col>
                    <Col xs={24} md={12} lg={10}>
                        <Space direction="horizontal" size="small" style={{ width: '100%' }}>
                            <DatePicker
                                value={selectedStartDate}
                                onChange={handleStartDateChange}
                                disabledDate={disabledStartDate}
                                placeholder="Select Sunday (Start)"
                                format="DD/MM/YYYY"
                                style={{ flex: 1 }}
                                suffixIcon={null}
                            />
                            <span style={{ margin: '0 4px' }}>→</span>
                            <DatePicker
                                value={selectedEndDate}
                                onChange={handleEndDateChange}
                                disabledDate={disabledEndDate}
                                placeholder="Select Sunday (End)"
                                format="DD/MM/YYYY"
                                style={{ flex: 1 }}
                                suffixIcon={null}
                                disabled={!selectedStartDate}
                            />
                        </Space>
                    </Col>
                    <Col xs={24} md={4}>
                        <Space>
                            <Button 
                            style={{ height: '30px' }}
                                type="primary" 
                                onClick={handleApplyDateRange}
                                disabled={!selectedStartDate || !selectedEndDate}
                                icon={<ReloadOutlined />}
                            >
                                Apply
                            </Button>
                            <Button 
                            style={{ height: '30px' }}
                                onClick={handleResetToCurrentWeek}
                            >
                                Reset
                            </Button>
                        </Space>
                    </Col>
                </Row>
                {dateError && (
                    <Row style={{ marginTop: 8 }}>
                        <Col span={24}>
                            <Alert message={dateError} type="error" showIcon closable onClose={() => setDateError('')} />
                        </Col>
                    </Row>
                )}
                {selectedStartDate && selectedEndDate && !dateError && (
                    <Row style={{ marginTop: 8 }}>
                        <Col span={24}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Showing data for week: {selectedStartDate.format('DD/MM/YYYY')} - {selectedEndDate.format('DD/MM/YYYY')}
                            </Text>
                        </Col>
                    </Row>
                )}
            </div>

            {/* FoodFlow Operational Performance - KPI CARDS */}
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ marginBottom: 16, fontSize: 20, fontWeight: 600 }}>
                    FoodFlow Operational Performance
                </h2>

                <Row gutter={[16, 16]}>

                <Col xs={24} sm={12} lg={6}>
                    <Card hoverable>
                        <Statistic
                            title="Opening Stock"
                            value={openingAndClosingStockData.openingStockValue}
                            precision={2}
                            prefix={<StockOutlined />}
                            valueStyle={{ fontSize: 28 }}
                        />
                        <div style={{ marginTop: 8 }}>
                            <Tag color="green"> 
                                on {dayjs(openingAndClosingStockData.openingStockDate).utc().format("DD/MM/YYYY")}
                            </Tag>
                        </div>
                    </Card>
                </Col>

                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable>
                          <Statistic
                                title="Total COGS"
                                value={(() => {
                                    const value = Number(kpis.totalCogs || 0.00);

                                    const normalStr = value.toLocaleString("fullwide", {
                                        useGrouping: false,
                                        maximumFractionDigits: 20,
                                    });

                                    const match = normalStr.match(/^-?\d+(?:\.\d{0,3})?/);

                                    return match ? match[0] : "0";
                                })()}
                                precision={2}
                                prefix={<EuroOutlined />}
                                valueStyle={{ fontSize: 28 }}
                            />

                            <div style={{ marginTop: 8 }}>
                                <Tag color={kpis.trendPctChange >= 0 ? "red" : "green"}>
                                    {kpis.trendPctChange >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                                    {" "}
                                     {(() => {
                                            const value = Number(kpis.trendPctChange || 0.00);

                                            const normalStr = value.toLocaleString("fullwide", {
                                                useGrouping: false,
                                                maximumFractionDigits: 20,
                                            });

                                            const match = normalStr.match(/^-?\d+(?:\.\d{0,1})?/);

                                            return match ? match[0] : "0";
                                        })()}% from last week
                                </Tag>
                            </div>
                        </Card>
                    </Col>


                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable>
                           <Statistic
                                title="Goods Used"
                                value={(() => {
                                    const value = Number(kpis.goodsUsed || 0);

                                    const normalStr = value.toLocaleString("fullwide", {
                                        useGrouping: false,
                                        maximumFractionDigits: 20,
                                    });

                                    const match = normalStr.match(/^-?\d+(?:\.\d{0,3})?/);

                                    return match ? match[0] : "0";
                                })()}
                                precision={2}
                                prefix={<ShoppingOutlined />}
                                valueStyle={{ fontSize: 28 }}
                            />

                            <div style={{ marginTop: 8 }}>
                                <Tag color="orange">
                                    {(() => {
                                            const value = Number(goodsUsedPercentage || 0.00);

                                            const normalStr = value.toLocaleString("fullwide", {
                                                useGrouping: false,
                                                maximumFractionDigits: 20,
                                            });

                                            const match = normalStr.match(/^-?\d+(?:\.\d{0,1})?/);

                                            return match ? match[0] : "0";
                                        })()}% of COGS
                                </Tag>
                            </div>
                        </Card>
                    </Col>


                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable>
                            <Statistic
                                title="Stock Adjustments"
                                value={kpis.stockAdjustments}
                                precision={2}
                                prefix={<SwapOutlined />}
                                valueStyle={{
                                    color: kpis.stockAdjustments >= 0 ? "#52c41a" : "#ff4d4f",
                                    fontSize: 28
                                }}
                            />

                            <div style={{ marginTop: 8 }}>
                                <Space size={0}>
                                    <Tag color={kpis.stockAdjustments >= 0 ? "green" : "red"}>
                                        {kpis.stockAdjustments >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                                        {" "}
                                        {kpis.stockAdjustments >= 0 ? "Up" : "Down"}
                                    </Tag>

                                    <Tag color="blue">
                                        {(() => {
                                            const value = Number(stockAdjustmentPercentage || 0.00);

                                            const normalStr = value.toLocaleString("fullwide", {
                                                useGrouping: false,
                                                maximumFractionDigits: 20,
                                            });

                                            const match = normalStr.match(/^-?\d+(?:\.\d{0,1})?/);

                                            return match ? match[0] : "0";
                                        })()} % Adjustment
                                    </Tag>
                                </Space>
                            </div>
                        </Card>
                    </Col>


                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable>
                            <Statistic
                                title="Total Waste"
                                value={(() => {
                                    const value = Number(kpis.totalWaste || 0);

                                    const normalStr = value.toLocaleString("fullwide", {
                                        useGrouping: false,
                                        maximumFractionDigits: 20,
                                    });

                                    const match = normalStr.match(/^-?\d+(?:\.\d{0,3})?/);

                                    return match ? match[0] : "0";
                                })()}
                                prefix={<DeleteOutlined />}
                                precision={2}
                                valueStyle={{ fontSize: 28 }}
                            />

                            <div style={{ marginTop: 8 }}>
                                <Tag color="magenta">
                                     {(() => {
                                    const value = Number(kpis.wastePercentage || 0.00);

                                    const normalStr = value.toLocaleString("fullwide", {
                                        useGrouping: false,
                                        maximumFractionDigits: 20,
                                    });

                                    const match = normalStr.match(/^-?\d+(?:\.\d{0,1})?/);

                                    return match ? match[0] : "0";
                                })()} % of COGS
                                </Tag>
                            </div>
                        </Card>
                    </Col>


                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable>
                            <Statistic
                                title="Production Waste"
                                value={(() => {
                                    const value = Number(kpis.productionWaste || 0);

                                    const normalStr = value.toLocaleString("fullwide", {
                                        useGrouping: false,
                                        maximumFractionDigits: 20,
                                    });

                                    const match = normalStr.match(/^-?\d+(?:\.\d{0,3})?/);

                                    return match ? match[0] : "0";
                                })()}
                                precision={2}
                                prefix={<ShoppingOutlined />}
                                valueStyle={{ fontSize: 28 }}
                            />

                            <div style={{ marginTop: 8 }}>
                                <Tag color="cyan">
                                     {(() => {
                                    const value = Number(productionWastePercentage || 0.00);

                                    const normalStr = value.toLocaleString("fullwide", {
                                        useGrouping: false,
                                        maximumFractionDigits: 20,
                                    });

                                    const match = normalStr.match(/^-?\d+(?:\.\d{0,1})?/);

                                    return match ? match[0] : "0";
                                })()} % of COGS
                                </Tag>
                            </div>
                        </Card>
                    </Col>

                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable>
                            <Statistic
                                title="Raw Waste"
                                  precision={2}
                                  value={(() => {
                                    const value = Number(kpis.rawWaste || 0);

                                    const normalStr = value.toLocaleString("fullwide", {
                                        useGrouping: false,
                                        maximumFractionDigits: 20,
                                    });

                                    const match = normalStr.match(/^-?\d+(?:\.\d{0,3})?/);

                                    return match ? match[0] : "0";
                                })()}
                                prefix={<DeleteOutlined />}
                                valueStyle={{ fontSize: 28 }}
                            />

                            <div style={{ marginTop: 8 }}>
                                <Tag color="green">
                                    {(() => {
                                    const value = Number(kpis.rawWastePercentage || 0.00);

                                    const normalStr = value.toLocaleString("fullwide", {
                                        useGrouping: false,
                                        maximumFractionDigits: 20,
                                    });

                                    const match = normalStr.match(/^-?\d+(?:\.\d{0,1})?/);

                                    return match ? match[0] : "0";
                                })()} % of COGS
                                </Tag>
                            </div>
                        </Card>
                    </Col> 

{/* 
                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable>
                            <Statistic
                                title="Waste % of COGS"
                                value={kpis.wastePercentage.toFixed(2)}
                                precision={2}
                                prefix={<DeleteOutlined />}
                                valueStyle={{ fontSize: 28 }}
                            />

                            <div style={{ marginTop: 8 }}>
                                <Tag color="green">
                                    {kpis.wastePercentage.toFixed(2)}% of COGS
                                </Tag>
                            </div>
                        </Card>
                    </Col> */}

                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable>
                            <Statistic
                                title="Closing Stock"
                                value={openingAndClosingStockData.closingStockValue}
                                precision={2}
                                prefix={<StockOutlined />}
                                valueStyle={{ fontSize: 28 }}
                            />

                            <div style={{ marginTop: 8 }}>
                            <Tag color="green">
                                    on{" "}
                                    {dayjs(
                                        dayjs().isBefore(dayjs(openingAndClosingStockData.closingStockDate), "day")
                                        ? dayjs()
                                        : openingAndClosingStockData.closingStockDate
                                    ).utc().format("DD/MM/YYYY")}
                                </Tag>
                            </div>
                        </Card>
                    </Col>

                </Row>
            </div>

            {/* CHARTS ROW - Waste Breakdown & Weekly Performance */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
    <Col xs={24} lg={12}>
        <Card title="Waste Breakdown" style={{ height: 420 }}>
            <ResponsiveContainer width="100%" height={340}>
                <PieChart margin={{ top: 10, right: 40, left: 20, bottom: 20 }}>
                   <Pie
                        data={wasteBreakdown}
                        dataKey="value"
                        cx="45%"
                        cy="50%"
                        outerRadius={115}
                        innerRadius={65}
                        // Remove the label prop completely
                    >
                        {wasteBreakdown.map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                        ))}
                    </Pie>
                    <RechartsTooltip
                        formatter={(value) => `${value.toFixed(1)}%`}
                    />
                    <Legend verticalAlign="bottom" height={36} />
                </PieChart>
            </ResponsiveContainer>
        </Card>
    </Col>

    <Col xs={24} lg={12}>
        <Card title="Weekly COGS Performance" style={{ height: 420 }}>
            <ResponsiveContainer width="100%" height={340}>
                <BarChart
                    data={weeklyData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" />
                    <YAxis />
                    <RechartsTooltip
                        formatter={(value) => `€${value.toFixed(2)}`}
                    />
                    <Legend />
                    <Bar
                        dataKey="totalCogs"
                        fill="#8884d8"
                        name="Total COGS"
                    />
                    <Bar
                        dataKey="goodsUsed"
                        fill="#52c41a"
                        name="Goods Used"
                    />
                    <Bar
                        dataKey="rawWaste"
                        fill="#faad14"
                        name="Raw Waste"
                    />
                    <Bar
                        dataKey="productionWaste"
                        fill="#ff7c43"
                        name="Production Waste"
                    />
                    <Bar
                        dataKey="stockAdjustments"
                        fill="#0055fe"
                        name="Stock Adjustments"
                    />
                </BarChart>
            </ResponsiveContainer>
        </Card>
    </Col>
</Row>

            {/* HOT HOLDING CHECKS & CLEANING COMPLIANCE */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} lg={12}>
                    <Card
                        title="Hot Holding Checks"
                        style={{ height: 450 }}
                        bodyStyle={{ padding: 24, height: "calc(100% - 57px)", overflowY: "auto" }}
                    >
                        {hotHoldData.units.length > 0 ? renderHotHoldGrid() : (
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
                                <Text type="secondary">No hot holding checks data available</Text>
                            </div>
                        )}
                    </Card>
                </Col>

                <Col xs={24} lg={12}>
                    <Card 
                        title="Cleaning Compliance" 
                        style={{ height: 450 }}
                        bodyStyle={{ padding: 24, height: "calc(100% - 57px)", overflowY: "auto" }}
                    >
                        <Row align="middle" style={{ height: "100%", minHeight: "300px" }}>
                            <Col span={10}>
                                <div style={{ textAlign: "center" }}>
                                    <h2 style={{ marginBottom: 20, color: "#1d3f72" }}>Daily</h2>
                                    <div style={{ 
                                        display: "grid", 
                                        gridTemplateColumns: "repeat(3, 24px)", 
                                        gap: 6, 
                                        justifyContent: "center", 
                                        marginBottom: 20 
                                    }}>
                                        {Array.from({ length: cleaningCompliance.totalTasks }).map((_, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    width: 24,
                                                    height: 24,
                                                    background: index < cleaningCompliance.tasksCompleted ? "#52c41a" : "#faad14",
                                                    borderRadius: 2
                                                }}
                                            />
                                        ))}
                                    </div>
                                    <Text strong style={{ fontSize: 16 }}>{cleaningCompliance.tasksCompleted} Tasks</Text><br />
                                    <Text type="secondary">at {cleaningCompliance.percent}%</Text>
                                </div>
                            </Col>
                            <Col span={14}>
                                <ResponsiveContainer width="100%" height={260}>
                                    <PieChart>
                                        <Pie
                                            data={[
                                                { name: "Completed", value: cleaningCompliance.percent },
                                                { name: "Remaining", value: 100 - cleaningCompliance.percent }
                                            ]}
                                            dataKey="value"
                                            innerRadius={70}
                                            outerRadius={100}
                                            startAngle={90}
                                            endAngle={-270}
                                        >
                                            <Cell fill={getArcColor()} />
                                            <Cell fill="#d9d9d9" />
                                        </Pie>
                                        <text
                                            x="50%"
                                            y="50%"
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                            style={{ fontSize: 34, fontWeight: 700, fill: getArcColor() }}
                                        >
                                            {cleaningCompliance.percent}%
                                        </text>
                                    </PieChart>
                                </ResponsiveContainer>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default OperationalDashboard;